/* CompanyRouteCard — a conditional city/company card with expandable role previews. */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BadgeCheck, MapPin, ChevronDown, AlertTriangle, Car, Wrench } from 'lucide-react';
import { RolePreviewCard } from './RolePreviewCard';
import { OfficialLink, Field, vehicleLabel, humanize, CITY_CARD_NOTICE } from './shared';

export function CompanyRouteCard({ c }) {
  const [open, setOpen] = useState(false);
  const vehicle = c.section === 'vehicle';
  const Icon = vehicle ? Car : Wrench;
  return (
    <motion.article initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-3xl border-2 transition-all ${open ? 'border-amber-400 shadow-lg' : 'border-gray-100 hover:border-amber-300 hover:shadow-md'}`}
      data-testid={`bc-company-${c.companyId}-${c.cityName.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="p-5">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <span className={`w-10 h-10 rounded-2xl flex items-center justify-center text-white shrink-0 ${vehicle ? 'bg-gradient-to-br from-sky-500 to-indigo-600' : 'bg-gradient-to-br from-amber-500 to-orange-600'}`}>
              <Icon size={18} />
            </span>
            <div className="min-w-0">
              <h3 className="font-extrabold text-gray-900 text-lg leading-tight">{c.companyName}</h3>
              <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1"><MapPin size={11} /> {c.cityName}</p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1 self-start text-[9px] font-extrabold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full max-w-full whitespace-normal text-left"
            data-testid={`bc-company-evidence-${c.companyId}-${c.cityName.toLowerCase().replace(/\s+/g, '-')}`}>
            <BadgeCheck size={10} className="shrink-0" /> {c.cityEvidenceLabel}
          </span>
        </div>

        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <Field label="Work model">{c.workModel}</Field>
          <Field label="Access category">{c.accessLabel}</Field>
          <Field label="Joining / registration fee status" testId={`bc-company-fee-${c.companyId}-${c.cityName.toLowerCase().replace(/\s+/g, '-')}`}>{c.joiningFeeLabel}</Field>
          <Field label="Vehicle required">{vehicleLabel(c.vehicleRequired)}</Field>
        </div>
        <div className="mt-3 rounded-xl bg-gray-50 border border-gray-100 p-3">
          <Field label="Official cost & asset status" testId={`bc-company-cost-${c.companyId}-${c.cityName.toLowerCase().replace(/\s+/g, '-')}`}>{c.officialCostAndAssetStatus || humanize(c.majorUpfrontInvestment)}</Field>
        </div>

        <p className="mt-4 text-xs text-amber-900 font-semibold flex items-start gap-1.5 bg-amber-50 border border-amber-200 rounded-xl p-3 leading-relaxed"
          data-testid={`bc-company-notice-${c.companyId}-${c.cityName.toLowerCase().replace(/\s+/g, '-')}`}>
          <AlertTriangle size={13} className="shrink-0 mt-0.5" /> {CITY_CARD_NOTICE}
        </p>
        {c.earningsPolicy && <p className="mt-2 text-[11px] text-gray-500 leading-relaxed">{c.earningsPolicy}</p>}

        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <span className="text-[10px] text-gray-400 font-semibold">
            Official route checked {c.checkedAt}
            {c.cityEvidenceUrl && <> · <a href={c.cityEvidenceUrl} target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600" data-testid={`bc-company-evidence-link-${c.companyId}-${c.cityName.toLowerCase().replace(/\s+/g, '-')}`}>city evidence</a></>}
          </span>
          <OfficialLink href={c.officialWorkerRouteUrl} label="Open official route and confirm city"
            testId={`bc-company-cta-${c.companyId}-${c.cityName.toLowerCase().replace(/\s+/g, '-')}`} />
        </div>
      </div>

      {c.rolePreviewCount > 0 && (
        <div className="border-t border-gray-100">
          <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
            className="w-full flex items-center justify-between px-5 py-3.5 text-sm font-extrabold text-amber-800 hover:bg-amber-50/60 rounded-b-3xl transition-colors"
            data-testid={`bc-roles-toggle-${c.companyId}-${c.cityName.toLowerCase().replace(/\s+/g, '-')}`}>
            <span>Explore work roles ({c.rolePreviewCount})</span>
            <ChevronDown size={16} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
          <AnimatePresence initial={false}>
            {open && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden" data-testid={`bc-roles-list-${c.companyId}-${c.cityName.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="px-5 pb-5 space-y-3">
                  <p className="text-[11px] text-gray-500 leading-relaxed">{c.rolePreviewDisplayRule}</p>
                  {c.rolePreviews.map((r) => <RolePreviewCard key={r.rolePreviewId} r={r} />)}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </motion.article>
  );
}
